import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAuthenticatedJWT } from '../utils/auth';
import { useIsAuthenticated } from '../hooks/useIsAuthenticated';
import { authenticateInIframe, isInIframe } from '../services/iframeAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

declare global {
  interface Window {
    microsoftTeams?: {
      app: {
        initialize: () => Promise<void>;
        getContext: () => Promise<any>;
      };
      authentication: {
        authenticate: (config: {
          url: string;
          width?: number;
          height?: number;
        }) => Promise<string>;
        notifySuccess: (result?: string) => void;
        notifyFailure: (reason?: string) => void;
      };
    };
  }
}

/**
 * Détection STRICTE du contexte Microsoft Teams.
 * On ne se base QUE sur l'URL contenant `teams.microsoft.com` pour éviter les faux positifs :
 * - `window.microsoftTeams` peut être pollué par une page parente qui charge teams-js sans être Teams.
 * - Le UA peut contenir "teams" pour des raisons indépendantes (ex : Edge sur certains tenants).
 * Le flow Teams (popup vers login.microsoftonline.com) ne fonctionne que dans le vrai client Teams.
 */
function isTeamsIframe(): boolean {
  try {
    if (!isInIframe()) return false;
    const isTeamsURL =
      window.location.href.includes('teams.microsoft.com') ||
      document.referrer.includes('teams.microsoft.com');
    return isTeamsURL;
  } catch (e) {
    return false;
  }
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { t } = useTranslation();
  const externalIsAuth = useIsAuthenticated();
  const [isAuth, setIsAuth] = useState(isAuthenticatedJWT());
  const [isInitializing, setIsInitializing] = useState(false);
  const [iframeError, setIframeError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  // Si l'auth se termine en dehors de ProtectedRoute (ex: useIframeAuthBoot
  // au démarrage de l'app, ou un autre composant qui appelle setJWT), on
  // synchronise l'état local pour passer à `children` sans attendre la
  // résolution du flow local.
  useEffect(() => {
    if (externalIsAuth && !isAuth) {
      setIsAuth(true);
      setIsInitializing(false);
      setIframeError(null);
    }
  }, [externalIsAuth, isAuth]);

  const handleRetry = useCallback(() => {
    setIframeError(null);
    setRetryNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (isAuth) return;

    const isTeams = isTeamsIframe();
    const inIframe = isInIframe();

    console.log('[ProtectedRoute] context detected', {
      isTeams,
      inIframe,
      href: window.location.href,
      referrer: document.referrer,
    });

    if (isTeams && typeof window.microsoftTeams !== 'undefined') {
      console.log('[ProtectedRoute] Teams iframe → Teams SDK auth flow');
      setIsInitializing(true);

      window.microsoftTeams.app.initialize().then(() => {
        const loginUrl = `${window.location.origin}/api/login/teams`;

        window.microsoftTeams!.authentication.authenticate({
          url: loginUrl,
          width: 600,
          height: 535
        }).then((token: string) => {
          document.cookie = `session_jwt=${token}; path=/; secure; samesite=none; max-age=${60 * 60 * 24 * 7}`;
          setIsAuth(true);
          setIsInitializing(false);
          window.location.reload();
        }).catch((error: any) => {
          console.error('Erreur authentification Teams:', error);
          setIsInitializing(false);
          if (error === 'USER_NOT_FOUND') {
            window.location.href = '/user-not-found';
          } else {
            window.location.href = '/error?status=401&message=' + encodeURIComponent(error || t('auth.authError'));
          }
        });
      }).catch((error: any) => {
        console.error('Erreur initialisation Teams:', error);
        setIsInitializing(false);
        window.location.href = '/api/login';
      });
    } else if (isTeams) {
      console.log('[ProtectedRoute] Teams iframe but SDK missing → loading teams-js');
      const script = document.createElement('script');
      script.src = 'https://res.cdn.office.net/teams-js/2.0.0/js/MicrosoftTeams.min.js';
      script.onload = () => {
        window.location.reload();
      };
      script.onerror = () => {
        window.location.href = '/api/login';
      };
      document.head.appendChild(script);
    } else if (inIframe) {
      console.log('[ProtectedRoute] iframe non-Teams → authenticateInIframe (no Keycloak redirect)');
      setIsInitializing(true);
      setIframeError(null);
      authenticateInIframe()
        .then((data) => {
          setIsAuth(true);
          setIsInitializing(false);
          if (!data.calendar_authorized) {
            window.location.href = '/authorize-calendar';
          }
        })
        .catch((error) => {
          console.warn('Iframe parent token failed:', error?.message || error);
          setIsInitializing(false);
          setIframeError(error?.message || t('auth.iframeTokenFailed'));
        });
    } else {
      if (inIframe) {
        console.error('[ProtectedRoute] BUG: iframe path fell through to Keycloak — aborting redirect');
        setIframeError(t('auth.iframeTokenFailed'));
        return;
      }
      console.log('[ProtectedRoute] not in iframe → redirect /api/login (Keycloak)');
      window.location.href = '/api/login';
    }
  }, [isAuth, t, retryNonce]);

  if (iframeError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <p className="text-gray-700 mb-4">{t('auth.iframeTokenFailed')}</p>
          <button
            type="button"
            onClick={handleRetry}
            className="px-4 py-2 bg-brand text-white rounded-md hover:opacity-90 transition"
          >
            {t('auth.iframeRetry')}
          </button>
        </div>
      </div>
    );
  }

  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
          <p className="text-gray-600">{t('auth.authenticating')}</p>
        </div>
      </div>
    );
  }

  if (!isAuth) {
    return null;
  }

  return <>{children}</>;
}
